import { IDataEvent } from "@multiweb/types/contracts";
import { Event } from "Global/Base/Events";
import { IOWObject } from "./DataTypes/IOWObject";
import { RequestContext } from "./RequestContext";

/**
 * Poskytuje prostředky pro prásci s workflow
 * @includeToDoc
 */
export interface IWorkflowManager {
    readonly openFormReceived: Event<IWorkflowManager, IWFOpenFormEventArgs>;

    /**
     * Spustí sledování vzniku požadavku na otevření formuláře z workflow
     */
    subscribeWFOpenForm(): void;
    /**
     * Zruší sledování vzniku požadavku na otevření formuláře z workflow
     */
    unsubscribeWFOpenForm(): void;
    /**
     * Dotazuje server na WF akce
     * @param object Aktuální objekt, pro který se dotazují WF akce
     * @param checkWf Příznak, zda se pro třídu objektu mají dotazovat práva
     * @param requestContext Viz {@link RequestContext}
     */
    getWFItems(object: IOWObject, checkWf: boolean, requestContext?: RequestContext): Promise<IWorkflowItemsResult>;
}

export class IWorkflowManager {
    private static _methods: (keyof IWorkflowManager)[] = [
        "subscribeWFOpenForm",
        "unsubscribeWFOpenForm",
        "getWFItems",
    ];
    private static _properties: (keyof IWorkflowManager)[] = [
        "openFormReceived",
    ];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null
            && !this._properties.some((value) => !Reflect.has(instance, value))
            && !this._methods.some(value => typeof instance[value] !== "function");
    }
}

export interface IWorkflowMenuItem {
    ItemName: string;
    ID: string;
    Codename: string;
    IconName: string;
    ParticipantsSelecting: boolean;

    /**
     * Ukazuje, jestli je potřeba před spouštěním WF acke zkontrolovat vyplnění odloženě povinných polí
     */
    ValidatePostReq: boolean;

    /**
     * Ukazuje, jestli je potřeba před spouštěním WF acke zkontrolovat vyplnění doporučených polí
     */
    ValidateRecommended: boolean;

    /**
     * Určuje, zda je možné při spouštění WorkFlow ihned vybírat konkrétní uživatele
     */
    ChooseFromUsersDefault: boolean;

    /**
     * Název nadřízené položky ve stromovém menu
     */
    MasterNode: string;

    NextActivityID: string;

    ParamFormName: string;
    LinkAttrForSelObj: string;
    SetValues: string;
    ConfirmMessage: string;
    Order: number;
}

export enum ParticipantType { Role = "role", User = "user" }

export interface IParticipantBase {
    readonly id: string;

}

export interface IParticipant extends IParticipantBase {
    readonly participantType: ParticipantType;
    readonly name: string;
}

export interface IUserParticipant extends IParticipant {
    readonly personName: string;
}

export interface IRoleParticipantsInformation extends IParticipant {
    readonly actualRoleName: string;
    readonly roleUsers: IUserParticipant[];
}

export interface IWFOpenFormParams {
    ActualInstanceID: string;
    FormName: string;
    Purpose: number;
    LinkAttr: string;
    Condition: string;
}

export class IWFOpenFormParams {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            Reflect.has(instance, "ActualInstanceID") &&
            Reflect.has(instance, "FormName") &&
            Reflect.has(instance, "Purpose") &&
            Reflect.has(instance, "LinkAttr") &&
            Reflect.has(instance, "Condition");
    }
}

/**
 * Zapouzdřuje data pro událost o otevření formuláře z WF
 * @param Zpráva DataEventu
 * @param Příznak, zda byla již zpráva celá zpracována
 */
export interface IWFOpenFormEventArgs {
    message: IDataEvent;
    handled: boolean;
}

export class IWFOpenFormEventArgs {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            Reflect.has(instance, "message") &&
            Reflect.has(instance, "handled");
    }
}

// ReSharper restore InconsistentNaming

export interface IWorkflowItemsResult {
    workflowMenuItems: IWorkflowMenuItem[];
    checkWorkflow: boolean;
}

export class IWorkflowItemsResult {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            Reflect.has(instance, "workflowMenuItems") &&
            Reflect.has(instance, "checkWorkflow");
    }
}
